import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";

let msalInstance = null;
let userAccounts = [];

export const initializeAuth = async (instance, accounts) => {
  msalInstance = instance;
  userAccounts = accounts;
};

/**
 * Acquires an access token silently, and if necessary, via popup interaction.
 * @param {PublicClientApplication} instance - The MSAL PublicClientApplication instance.
 * @param {Array} accounts - The array of user accounts.
 * @returns {Promise<string|null>} - A promise that resolves to the access token or null.
 */
export const getAccessToken = async () => {
  if (!msalInstance) {
    console.error("MSAL instance is not available");
    return null;
  }

  if (!userAccounts || userAccounts.length === 0) {
    console.error("No accounts available");
    return null;
  }

  const account = userAccounts[0];

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup({
          ...loginRequest,
          account: account,
        });
        return response.accessToken;
      } catch (popupError) {
        console.error("Token acquisition via popup failed:", popupError);
        return null;
      }
    } else {
      console.error("Silent token acquisition failed:", error);
      return null;
    }
  }
};

/**
 * Makes an authenticated API call using the provided access token.
 * @param {string} accessToken - The access token for authorization.
 * @param {string} apiEndpoint - The API endpoint to call.
 * @param {object} [options={}] - Optional configurations for the API call.
 * @returns {Promise<any|null>} - A promise that resolves to the API response data or null.
 */
export const fetchSecure = async (accessToken, apiEndpoint, options = {}) => {
  if (!accessToken) {
    console.error("Access token is not available");
    return null;
  }

  try {
    const response = await axios({
      method: options.method || "GET",
      url: apiEndpoint,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...options.headers,
      },
      data: options.data || null,
      params: options.params || null,
    });
    return response.data;
  } catch (error) {
    console.error("API call failed:", error);
    return null;
  }
};

export const saveAppDetails = async (appDetails) => {
  console.log('=======> saveAppDetails');

  if (!appDetails) throw new Error("Application Details is required.");
  const API_URL = process.env.REACT_APP_API_URL || "";
 
  const url = appDetails.id ? `${API_URL}/engineering/appcenter/update` : `${API_URL}/engineering/appcenter/post`;

  const options = {
    method:  appDetails.id ? 'PUT' : 'POST',
    data: appDetails,
  };
  const accessToken = await getAccessToken();
  const response = await fetchSecure(accessToken, url, options);
  console.log('=======> saveAppDetails data posted');
  return response;
};

export const axiosFetch = {
  get: async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return { data: await response.json() };
  },
  put: async (url, data) => {
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return { data: await response.json() };
  }
};